// Shared enums / constants for the VAPT module.
// Kept framework-agnostic (plain JS objects/arrays) so both the mock service
// layer and the UI components can import from a single source of truth.

export const VAPT_DOMAINS = [
  "Web",
  "API",
  "Mobile",
  "Infrastructure",
  "Cloud",
  "Source Code",
  "Kubernetes",
];

export const VAPT_SEVERITIES = ["Critical", "High", "Medium", "Low", "Informational"];

export const VAPT_SEVERITY_ORDER = {
  Critical: 0,
  High: 1,
  Medium: 2,
  Low: 3,
  Informational: 4,
};

export const VAPT_VALIDATION_STATUSES = [
  "Pending Validation",
  "Validated",
  "Confirmed",
  "False Positive",
  "Duplicate",
  "Accepted Risk",
];

export const VAPT_REMEDIATION_STATUSES = [
  "Open",
  "Assigned",
  "In Progress",
  "Fixed",
  "Ready for Retest",
  "Retest Failed",
  "Retest Passed",
  "Closed",
  "Resolved",
  "Accepted Risk",
  "Not Applicable",
];

export const VAPT_SCAN_STATUSES = [
  "Draft",
  "Queued",
  "Running",
  "Completed",
  "Partially Completed",
  "Failed",
  "Cancelled",
  "Paused",
];

export const VAPT_WORKER_STATUSES = ["Online", "Offline", "Busy", "Error", "Maintenance"];

export const VAPT_PROJECT_STATUSES = [
  "Planning",
  "Active",
  "On Hold",
  "Completed",
  "Archived",
];

// Tool lists by domain (must match exactly per spec)
export const VAPT_TOOLS_BY_DOMAIN = {
  Web: ["Burp Suite Pro", "Nuclei", "Nmap", "ffuf", "OWASP ZAP"],
  API: ["Burp Suite Pro", "Postman", "Nuclei", "Schemathesis"],
  Mobile: ["MobSF", "Frida", "Objection", "JADX", "apktool", "Burp Suite"],
  Infrastructure: ["Nessus/Qualys", "Nmap", "Metasploit", "Wireshark", "NetExec"],
  Cloud: ["Prowler", "ScoutSuite", "Trivy", "Checkov", "Pacu"],
  "Source Code": ["Semgrep", "SonarQube", "Gitleaks", "Snyk", "OWASP Dependency-Check"],
  Kubernetes: ["Trivy", "kube-bench", "kube-hunter"],
};

export const VAPT_SCAN_TEMPLATES = [
  { id: "quick_scan", name: "Quick Scan", description: "Fast, lightweight recon-level scan", domain: null },
  { id: "full_vapt", name: "Full VAPT", description: "Comprehensive assessment using the full domain tool bundle", domain: null },
  { id: "custom_scan", name: "Custom Scan", description: "Manually pick tools and configuration", domain: null },
  { id: "web_full_vapt", name: "Web Full VAPT", description: "Full web application VA/PT bundle", domain: "Web" },
  { id: "api_full_vapt", name: "API Full VAPT", description: "Full API security testing bundle", domain: "API" },
  { id: "mobile_vapt", name: "Mobile VAPT", description: "Full mobile app security testing bundle", domain: "Mobile" },
  { id: "infra_vapt", name: "Infrastructure VAPT", description: "Full infrastructure/network testing bundle", domain: "Infrastructure" },
  { id: "cloud_vapt", name: "Cloud VAPT", description: "Full cloud security posture testing bundle", domain: "Cloud" },
  { id: "source_code_vapt", name: "Source Code VAPT", description: "Full SAST/secret-scan bundle", domain: "Source Code" },
  { id: "kubernetes_vapt", name: "Kubernetes VAPT", description: "Full container/K8s security bundle", domain: "Kubernetes" },
];

export const VAPT_DOMAIN_ICON_COLOR = {
  Web: "blue",
  API: "green",
  Mobile: "pink",
  Infrastructure: "purple",
  Cloud: "cyan",
  "Source Code": "indigo",
  Kubernetes: "teal",
};

// Text-only severity color (no background/border/pill) — mirrors the status
// text treatment used across VAPT so severity never renders as a filled badge.
export const severityBadgeColor = (severity) => {
  switch ((severity || "").toLowerCase()) {
    case "critical":
      return "text-red-700";
    case "high":
      return "text-orange-600";
    case "medium":
      return "text-amber-600";
    case "low":
      return "text-green-700";
    case "informational":
      return "text-blue-700";
    default:
      return "text-gray-500";
  }
};

// Text-only status color (no background/border/pill) — used by VaptStatusBadge
// across every VAPT screen so status stays a plain, highlighted text label,
// never a button/badge-looking element.
export const statusBadgeColor = (status) => {
  const s = (status || "").toLowerCase();
  if (["completed", "fixed", "closed", "resolved", "retest passed", "online", "confirmed", "validated", "scanned", "sent"].includes(s)) {
    return "text-green-700";
  }
  if (["running", "in progress", "busy", "assigned", "completed with exceptions"].includes(s)) {
    return "text-blue-700";
  }
  if (["queued", "pending", "pending validation", "ready for retest", "paused", "draft", "unsupported", "partially completed", "partially fixed"].includes(s)) {
    return "text-amber-600";
  }
  if (["failed", "error", "retest failed", "open", "blocked"].includes(s)) {
    return "text-red-700";
  }
  if (["accepted risk"].includes(s)) {
    return "text-purple-700";
  }
  if (["cancelled", "maintenance", "false positive", "duplicate", "offline", "not applicable", "out of scope"].includes(s)) {
    return "text-gray-500";
  }
  return "text-gray-500";
};
